import { useCallback, useEffect, useState } from "react";
import { Link, useRoute } from "wouter";
import {
  ArrowLeft,
  Download,
  FileSignature,
  Gavel,
  Loader2,
  Maximize2,
  Minimize2,
  PenLine,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArbiterShell } from "@/components/ArbiterShell";
import { FormationGate } from "@/components/FormationGate";
import { AssistantIAPanel } from "@/components/AssistantIAPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProofBadge } from "@/components/ProofBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth, useIsArbiterOrAdmin } from "@/contexts/AuthContext";
import { classifyApiError } from "@/hooks/useApiErrorState";
import {
  createDecision,
  type DecisionOut,
  type DecisionTypeKind,
  downloadDecisionPdf,
  downloadEvidenceBundle,
  getApiErrorDetail,
  listCaseDecisions,
  signDecision,
  updateDecision,
} from "@/lib/api";

const TYPE_LABELS: Record<DecisionTypeKind, string> = {
  PROCEDURAL_ORDER: "Ordonnance de procédure",
  PARTIAL_AWARD: "Sentence partielle",
  FINAL_AWARD: "Sentence finale",
};

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; detail: string }
  | { kind: "ready"; decisions: DecisionOut[] };

export default function CaseDecisions() {
  const [match, params] = useRoute<{ caseId: string }>(
    "/cases/:caseId/decisions",
  );
  const { session } = useAuth();
  const isArbiterOrAdmin = useIsArbiterOrAdmin();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [focused, setFocused] = useState(false);

  const [newType, setNewType] =
    useState<DecisionTypeKind>("PROCEDURAL_ORDER");
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [creating, setCreating] = useState(false);

  const caseIdRaw = params?.caseId;
  const caseId = caseIdRaw ? Number.parseInt(caseIdRaw, 10) : Number.NaN;

  const reload = useCallback(async () => {
    if (!Number.isFinite(caseId)) {
      setState({ kind: "error", detail: "Identifiant invalide." });
      return;
    }
    try {
      const decisions = await listCaseDecisions(caseId);
      setState({ kind: "ready", decisions });
    } catch (err) {
      const c = classifyApiError(err);
      const errDetail = c.kind !== "none" ? c.detail : "Echec de chargement des décisions.";
      setState({
        kind: "error",
        detail: errDetail,
      });
    }
  }, [caseId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Raccourcis Focus Mode :
  //   Alt+F   -> toggle (entrer / sortir)
  //   Escape  -> sortir si actif (préserve la convention modale)
  // Pas de listener cross-OS Cmd : Alt est plus prévisible que Cmd qui
  // entre en collision avec la palette ⌘K du shell.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        setFocused((f) => !f);
      } else if (e.key === "Escape" && focused) {
        setFocused(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focused]);

  const onCreate = async () => {
    if (
      !Number.isFinite(caseId) ||
      newTitle.trim().length < 3 ||
      newContent.trim().length < 10 ||
      creating
    ) {
      return;
    }
    setCreating(true);
    try {
      await createDecision({
        case_file_id: caseId,
        decision_type: newType,
        title: newTitle.trim(),
        content_markdown: newContent.trim(),
      });
      toast.success("Brouillon de décision créé.");
      setNewTitle("");
      setNewContent("");
      void reload();
    } catch (err) {
      const c = classifyApiError(err);
      const errDetail = c.kind !== "none" ? c.detail : "Echec création.";
      toast.error(errDetail);
    } finally {
      setCreating(false);
    }
  };

  if (!match) return null;

  const myUserId = session?.user.id;

  const title = `Décisions · Dossier #${caseIdRaw}`;
  const rightPanel = Number.isFinite(caseId) ? (
    <AssistantIAPanel
      caseFileId={caseId}
      scopeHint="Espace délibéré — secret du délibéré strict. L'assistant ne reçoit ni n'envoie aucune donnée vers les parties."
    />
  ) : undefined;

  const focusToggle = (
    <Button
      variant={focused ? "outline" : "ghost"}
      size="icon"
      onClick={() => setFocused((f) => !f)}
      aria-label={
        focused ? "Quitter le mode focus délibéré" : "Activer le mode focus délibéré"
      }
      title={focused ? "Quitter (Esc)" : "Mode focus délibéré (Alt+F)"}
      style={
        focused
          ? {
              color: "var(--proof-gold)",
              borderColor: "var(--proof-gold)",
            }
          : undefined
      }
    >
      {focused ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
    </Button>
  );

  return (
    <ArbiterShell
      title={title}
      rightPanel={rightPanel}
      focused={focused}
      topBarExtra={focusToggle}
    >
      <FormationGate parcours="ARBITRE" feature="decision_validation">
      {/* Header dédié à la page : breadcrumb retour au dossier + bandeau focus */}
      <div
        className="sticky top-12 z-10 px-4 sm:px-6 py-2 border-b border-border flex items-center gap-3 backdrop-blur-sm"
        style={{
          background:
            "color-mix(in oklch, var(--graphite-900) 92%, transparent)",
        }}
      >
        <Link
          href={`/cases/${caseIdRaw}`}
          className="inline-flex items-center gap-1 text-caption-arbitre"
          style={{ color: "var(--proof-gold)" }}
        >
          <ArrowLeft size={11} />
          Retour au dossier
        </Link>
        {focused && (
          <span
            className="ml-auto text-caption-arbitre flex items-center gap-1.5"
            style={{ color: "var(--proof-gold)" }}
          >
            <ShieldCheck size={11} />
            Mode focus délibéré · Esc pour sortir
          </span>
        )}
      </div>

      <div
        className={`flex-1 px-4 sm:px-6 py-6 sm:py-8 mx-auto w-full space-y-6 sm:space-y-8 ${
          focused ? "max-w-4xl" : "max-w-3xl"
        }`}
      >
        {state.kind === "loading" && (
          <div className="flex items-center justify-center py-16">
            <Loader2
              className="animate-spin"
              size={28}
              style={{ color: "var(--proof-gold)" }}
            />
          </div>
        )}

        {state.kind === "error" && (
          <div
            className="rounded-md border p-6 text-sm"
            style={{
              borderColor:
                "color-mix(in oklch, var(--block-red) 40%, transparent)",
              background:
                "color-mix(in oklch, var(--block-red) 10%, transparent)",
              color: "rgb(254 202 202)",
            }}
          >
            {state.detail}
            <div className="mt-3">
              <Button onClick={() => void reload()} size="sm">
                Réessayer
              </Button>
            </div>
          </div>
        )}

        {state.kind === "ready" && (
          <>
            {isArbiterOrAdmin && !focused && (
              <>
                <div
                  className="rounded-md border p-3 flex items-start gap-3 flex-wrap"
                  style={{
                    borderColor:
                      "color-mix(in oklch, var(--ai-violet) 30%, transparent)",
                    background:
                      "color-mix(in oklch, var(--ai-violet) 8%, transparent)",
                  }}
                >
                  <div className="flex-1 min-w-[200px]">
                    <div
                      className="text-caption-arbitre"
                      style={{ color: "var(--ai-violet)" }}
                    >
                      GSF Sentence Companion
                    </div>
                    <p
                      className="text-sm mt-0.5"
                      style={{ color: "var(--arbitre-slate-100)" }}
                    >
                      Redaction assistee par IA des 8 sections Art. 20 AUA
                      avec validation HITL obligatoire avant signature.
                    </p>
                  </div>
                  <Link href={`/cases/${caseId}/sentence`}>
                    <Button
                      size="sm"
                      style={{
                        background: "var(--ai-violet)",
                        color: "var(--arbitre-slate-100)",
                      }}
                    >
                      <PenLine size={14} className="mr-1.5" />
                      Ouvrir le brouillon IA
                    </Button>
                  </Link>
                </div>

                <CreateDraftForm
                  newType={newType}
                  setNewType={setNewType}
                  newTitle={newTitle}
                  setNewTitle={setNewTitle}
                  newContent={newContent}
                  setNewContent={setNewContent}
                  creating={creating}
                  onCreate={onCreate}
                />
              </>
            )}

            <section className="space-y-3">
              <h2
                className="text-caption-arbitre"
                style={{ color: "var(--proof-gold)" }}
              >
                {state.decisions.length === 0
                  ? "Aucune décision rendue"
                  : `${state.decisions.length} décision${state.decisions.length > 1 ? "s" : ""}`}
              </h2>
              {state.decisions.map((d) => (
                <DecisionRow
                  key={d.id}
                  decision={d}
                  isAuthor={!!myUserId && d.author_user_id === myUserId}
                  isArbiterOrAdmin={isArbiterOrAdmin}
                  onChanged={() => void reload()}
                />
              ))}
            </section>
          </>
        )}
      </div>
      </FormationGate>
    </ArbiterShell>
  );
}

// =============================================================================
// Sous-composants
// =============================================================================

function CreateDraftForm({
  newType,
  setNewType,
  newTitle,
  setNewTitle,
  newContent,
  setNewContent,
  creating,
  onCreate,
}: {
  newType: DecisionTypeKind;
  setNewType: (v: DecisionTypeKind) => void;
  newTitle: string;
  setNewTitle: (v: string) => void;
  newContent: string;
  setNewContent: (v: string) => void;
  creating: boolean;
  onCreate: () => void;
}) {
  return (
    <section className="gsf-surface p-5 sm:p-6 space-y-4">
      <div
        className="flex items-center gap-2 text-caption-arbitre"
        style={{ color: "var(--proof-gold)" }}
      >
        <PenLine size={12} />
        Rédiger un nouveau brouillon
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label
            htmlFor="decision-type"
            className="text-caption-arbitre"
            style={{ color: "var(--proof-gold)" }}
          >
            Type
          </Label>
          <Select
            value={newType}
            onValueChange={(v) => setNewType(v as DecisionTypeKind)}
            disabled={creating}
          >
            <SelectTrigger id="decision-type" className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PROCEDURAL_ORDER">
                {TYPE_LABELS.PROCEDURAL_ORDER}
              </SelectItem>
              <SelectItem value="PARTIAL_AWARD">
                {TYPE_LABELS.PARTIAL_AWARD}
              </SelectItem>
              <SelectItem value="FINAL_AWARD">
                {TYPE_LABELS.FINAL_AWARD}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label
            htmlFor="decision-title"
            className="text-caption-arbitre"
            style={{ color: "var(--proof-gold)" }}
          >
            Titre
          </Label>
          <Input
            id="decision-title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Ex : Ordonnance n° 3 sur le calendrier procédural"
            className="mt-1"
            disabled={creating}
          />
        </div>
      </div>
      <div>
        <Label
          htmlFor="decision-content"
          className="text-caption-arbitre"
          style={{ color: "var(--proof-gold)" }}
        >
          Contenu (Markdown)
        </Label>
        <Textarea
          id="decision-content"
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          rows={8}
          placeholder={
            "# Sur la recevabilité\n\nVu les pièces versées au dossier..."
          }
          className="mt-1 resize-y gsf-mono-proof text-sm"
          disabled={creating}
        />
      </div>
      <div className="flex justify-end">
        <Button
          onClick={onCreate}
          disabled={
            creating ||
            newTitle.trim().length < 3 ||
            newContent.trim().length < 10
          }
          style={{
            background: "var(--proof-gold)",
            color: "var(--graphite-900)",
          }}
        >
          {creating ? (
            <>
              <Loader2 size={14} className="animate-spin mr-2" />
              Création…
            </>
          ) : (
            <>
              <Gavel size={14} className="mr-2" />
              Créer le brouillon
            </>
          )}
        </Button>
      </div>
    </section>
  );
}

function DecisionRow({
  decision,
  isAuthor,
  isArbiterOrAdmin,
  onChanged,
}: {
  decision: DecisionOut;
  isAuthor: boolean;
  isArbiterOrAdmin: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(decision.title);
  const [content, setContent] = useState(decision.content_markdown);
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const isSigned = decision.status === "SIGNED";
  const canEdit = isAuthor && !isSigned;
  const canSign = isAuthor && !isSigned && isArbiterOrAdmin;

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await updateDecision(decision.id, {
        title: title.trim(),
        content_markdown: content.trim(),
      });
      toast.success("Brouillon mis à jour.");
      setEditing(false);
      onChanged();
    } catch (err) {
      const c = classifyApiError(err);
      const errDetail = c.kind !== "none" ? c.detail : getApiErrorDetail(err) ?? "Echec mise à jour.";
      toast.error(errDetail);
    } finally {
      setSaving(false);
    }
  };

  const onSign = async () => {
    if (signing) return;
    setSigning(true);
    try {
      const res = await signDecision(decision.id);
      toast.success(
        `Décision signée. ${
          res.is_simulated ? "(QES simulée)" : "(QES qualifiée Confidens)"
        }`,
      );
      onChanged();
    } catch (err) {
      const c = classifyApiError(err);
      const errDetail = c.kind !== "none" ? c.detail : getApiErrorDetail(err) ?? "Echec signature.";
      toast.error(errDetail);
    } finally {
      setSigning(false);
    }
  };

  const onDownload = async () => {
    if (!decision.evidence_bundle_id) return;
    try {
      await downloadDecisionPdf(
        decision.id,
        `decision-${decision.evidence_bundle_id}.bundle`,
      );
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? "Téléchargement impossible.");
    }
  };

  const onDownloadEvidence = async () => {
    const bundleId =
      decision.evidence_bundle_id ?? `decision-${decision.id}`;
    try {
      await downloadEvidenceBundle(
        decision.id,
        `evidence-bundle-${bundleId}.zip`,
      );
      toast.success("Bundle probatoire téléchargé.");
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? "Téléchargement impossible.");
    }
  };

  return (
    <article className="gsf-surface p-5">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div
            className="text-caption-arbitre flex items-center gap-2 flex-wrap mb-2"
            style={{ color: "var(--proof-gold)" }}
          >
            <span>{decision.decision_type}</span>
            {isSigned ? (
              <ProofBadge
                variant="filled"
                icon={<ShieldCheck size={10} />}
                title="Signature qualifiée Confidens (QES eIDAS)"
              >
                QES
              </ProofBadge>
            ) : (
              <ProofBadge variant="missing" title="Brouillon non signé">
                Brouillon
              </ProofBadge>
            )}
            <span
              className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold"
              style={{
                background: isSigned
                  ? "var(--validate-green)"
                  : "color-mix(in oklch, var(--alert-orange) 25%, transparent)",
                color: isSigned ? "var(--graphite-900)" : "var(--alert-orange)",
                letterSpacing: "0.05em",
              }}
            >
              {decision.status}
            </span>
          </div>
          <h3 className="text-h3-arbitre">{decision.title}</h3>
          <div
            className="mt-1 text-xs flex flex-wrap gap-3"
            style={{ color: "var(--arbitre-slate-300)" }}
          >
            <span>
              Créé le{" "}
              {new Date(decision.created_at).toLocaleDateString("fr-FR")}
            </span>
            {decision.signed_at && (
              <span style={{ color: "var(--validate-green)" }}>
                Signée le{" "}
                {new Date(decision.signed_at).toLocaleString("fr-FR")}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-2 flex-wrap shrink-0">
          {canEdit && !editing && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditing(true)}
              style={{ color: "var(--proof-gold)" }}
            >
              <PenLine size={13} className="mr-1" />
              Éditer
            </Button>
          )}
          {canSign && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  style={{
                    background: "var(--proof-gold)",
                    color: "var(--graphite-900)",
                  }}
                  disabled={signing}
                >
                  <FileSignature size={13} className="mr-1" />
                  Signer (QES)
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    Signature qualifiée Confidens
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="text-sm space-y-2">
                      <p>
                        Vous êtes sur le point de signer cette décision avec
                        une <strong>signature électronique qualifiée
                        eIDAS</strong> via Confidens (QTSP Zetes).
                      </p>
                      <p>
                        Une fois signée, la décision devient
                        <strong> immuable</strong> — toute modification
                        casserait le hash signé et invaliderait la preuve.
                        En production, votre app mobile Confidens recevra
                        une demande de confirmation (authentification forte
                        eIDAS QES).
                      </p>
                      <p style={{ color: "var(--proof-gold)" }}>
                        Mode actuel : voir le bandeau après signature
                        (simulation = non opposable, production = opposable
                        eIDAS).
                      </p>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={signing}>
                    Annuler
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.preventDefault();
                      void onSign();
                    }}
                    disabled={signing}
                    style={{
                      background: "var(--proof-gold)",
                      color: "var(--graphite-900)",
                    }}
                  >
                    {signing ? (
                      <>
                        <Loader2
                          size={13}
                          className="animate-spin mr-2"
                        />
                        Signature…
                      </>
                    ) : (
                      "Confirmer la signature"
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {isSigned && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void onDownload()}
              style={{ color: "var(--proof-gold)" }}
            >
              <Download size={13} className="mr-1" />
              Bundle signé
            </Button>
          )}
          {/* Bundle probatoire ZIP (PR 10) — disponible des l'etat DRAFT. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => void onDownloadEvidence()}
            title="Bundle probatoire : ZIP autonome (DOCX + PDF + chaine audit + sources RAG + rapport Art. 20). Verifiable hors-ligne via le manifest SHA-256."
          >
            <Download size={13} className="mr-1" />
            Bundle probatoire
          </Button>
        </div>
      </header>

      {editing ? (
        <div className="mt-4 space-y-3">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={saving}
          />
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={10}
            className="gsf-mono-proof text-sm"
            disabled={saving}
          />
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setTitle(decision.title);
                setContent(decision.content_markdown);
                setEditing(false);
              }}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={
                saving ||
                title.trim().length < 3 ||
                content.trim().length < 10
              }
              style={{
                background: "var(--proof-gold)",
                color: "var(--graphite-900)",
              }}
            >
              {saving ? (
                <>
                  <Loader2 size={13} className="animate-spin mr-2" />
                  Sauvegarde…
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </div>
        </div>
      ) : (
        <pre
          className={`mt-3 text-xs whitespace-pre-wrap gsf-mono-proof p-3 rounded-md${
            isSigned ? "" : " draft-watermark"
          }`}
          data-watermark={isSigned ? undefined : "Projet — non signé"}
          style={{
            background: "var(--graphite-900)",
            color: "var(--arbitre-slate-100)",
            border: "1px solid var(--graphite-600)",
            lineHeight: 1.55,
          }}
        >
          {decision.content_markdown}
        </pre>
      )}

      {isSigned && (
        <div
          className="mt-4 pt-3 digital-seal text-xs space-y-1.5 gsf-mono-proof"
          style={{
            color: "var(--arbitre-slate-500)",
          }}
        >
          {/* Sceau numérique — cf. docs/DESIGN_SOLENNEL_SENTENCE.md §2-3. */}
          <div
            className="text-caption-arbitre flex items-center gap-2"
            style={{ color: "var(--proof-gold)" }}
          >
            <ShieldCheck
              size={11}
              style={{ color: "var(--validate-green)" }}
            />
            <span>
              Sceau numérique — Signature électronique qualifiée (QES) ·
              eIDAS Art. 26
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span style={{ color: "var(--proof-gold)" }}>signature_id</span>
            <span className="break-all">{decision.confidens_signature_id}</span>
          </div>
          {decision.signed_pdf_hash && (
            <div className="flex flex-wrap items-center gap-2">
              <span style={{ color: "var(--proof-gold)" }}>hash</span>
              <span className="break-all">{decision.signed_pdf_hash}</span>
            </div>
          )}
          {decision.evidence_bundle_id && (
            <div className="flex flex-wrap items-center gap-2">
              <span style={{ color: "var(--proof-gold)" }}>bundle</span>
              <span className="break-all">
                {decision.evidence_bundle_id}
              </span>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
